import { useEffect, useRef } from 'react';

import { requestLauncherReload } from '@/logic/web-wrapper/launcher-embedded-navigation';
import { useStore } from '@/store/useStore';
import { setUserSessionContext } from '@/store/actions';

const RELOAD_DELAY_MS = 5000;

/**
 * Recovers from transient connectivity loss. When the connection drops we flag
 * `wasOnline`; once it is restored, if the game has not finished loading we
 * reload the page after a short delay so the user lands on a fresh, working
 * session instead of a half-loaded one.
 */
export const useConnectivityReload = () => {
  const wasOnline = useStore((s) => s.userSessionContext.wasOnline);
  const gameLoaded = useStore((s) => s.gameStatus.gameLoadSuccess);
  const reloadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleOffline = () => {
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
        reloadTimeoutRef.current = null;
      }
      if (!wasOnline) {
        setUserSessionContext({ wasOnline: true });
      }
    };

    const handleOnline = () => {
      if (wasOnline && !gameLoaded && !reloadTimeoutRef.current) {
        reloadTimeoutRef.current = setTimeout(() => {
          setUserSessionContext({ wasOnline: false });
          reloadTimeoutRef.current = null;
          void requestLauncherReload();
        }, RELOAD_DELAY_MS);
      }
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [wasOnline, gameLoaded]);
};
